import { ProgressBar, QuestionsContextProvider, StepType, formModelGet } from '@portal/ui-library';
import { useApplicationHelpers } from 'hooks/useApplicationHelpers';
import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { WizardLayout } from 'layout/wizard';
import { useURLParams } from 'hooks/useURLParams';
import { WizardScreen } from './WizardRouter';
import { WizardRoutesEnum } from 'utils/actions';
import { WizardSlot } from 'layout/wizard/slots';
import { Loading } from 'components/Loading';
import {
  HealthInsuranceContextProvider,
  useHealthInsuranceContext
} from 'contexts/HealthInsuranceContext';
import { WizardNavigationContextProvider, useWizardNavigation } from 'hooks/useWizardNavigation';

import { ApiError, Tariff } from 'models/extension-generated';
import { partnerField } from 'models';

import { Sidebar } from 'components/Sidebar';
import { TariffContextProvider } from 'contexts/TariffSectionContext';
import { WhiteLogo } from 'components/Logo';
import { getQuestionsByCriteria } from 'utils/getQuestionsByCriteria';
import { LegalFooter } from 'components/LegalFooter';
import ContactSupport from 'components/ContactSupport';
import { useApplicationValidation } from 'hooks/useApplicationValidation';

export function Content() {
  const { t } = useTranslation('wizardPrivateHealthInsurance');
  useURLParams();
  const isLoading = false;
  const { currentScreen, currentSection, completeSections, goTo, ScreenOrder } =
    useWizardNavigation();
  const { state, reducer, partnerId } = useHealthInsuranceContext();
  const { validateNextPage } = useApplicationValidation(state, reducer, partnerId);
  const { getPartnerById } = useApplicationHelpers(state.model);

  const tariffsPath = partnerField(
    partnerId,
    'applicationInformation.tariffInformation.selectedTariffs'
  );
  const selectedTariffs: Array<Tariff> = formModelGet(state.model, tariffsPath) || [];
  const currentPartner = getPartnerById(partnerId);
  const birthDate = currentPartner.birthDate;
  const insuranceStart = state.model.applicationStart || '';

  const isOnInsuredPersonScreen = currentScreen === WizardRoutesEnum.PrivateData;

  async function handleProgressBarChange(index: number, goTo: (screen: WizardRoutesEnum) => void) {
    try {
      const nextPage = ScreenOrder[index];
      const validationResult = await validateNextPage(nextPage);
      const hasNoErrors = validationResult.size === 0;

      if (hasNoErrors) {
        switch (index) {
          case 0:
            goTo(WizardRoutesEnum.PrivateData);
            return;
          case 1:
            goTo(WizardRoutesEnum.InsuranceHolder);
            return;
          case 2:
            goTo(WizardRoutesEnum.Summary);
            return;
        }
      } else {
        setTimeout(() => {
          const validationErrorButton = document.getElementById('validationErrorButton');
          if (validationErrorButton) {
            validationErrorButton.focus();
          }
        }, 500);
      }
    } catch (e) {
      if (e instanceof Error || e instanceof ApiError) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    }
  }

  const fetchQuestionsFn = async ({
    tariffs,
    birthDate,
    insuranceStart
  }: {
    tariffs: Array<Tariff>;
    birthDate: string;
    insuranceStart: string;
  }) => {
    const questions = await getQuestionsByCriteria({
      tariffs,
      hasBirthCriteria: true,
      birthDate,
      insuranceStart
    });
    if (isOnInsuredPersonScreen) {
      reducer.createQuestions({
        healthKey: partnerField(partnerId, 'applicationInformation.health'),
        healthQuestions: questions
      });
    }
    return questions;
  };

  const progressSteps: StepType[] = useMemo(
    () => [
      {
        step: 1,
        label: t('navigation.steps.insuredPeople'),

        ariaDescription:
          t('navigation.steps.insuredPeople') +
          ': Wählen Sie pro versicherter Person einen oder mehrere Tarife, füllen Sie allgemeine Personendaten und die Gesundheitsangaben abhängig von den Tarifen aus.'
      },
      {
        step: 2,
        label: t('navigation.steps.holder'),
        ariaDescription:
          t('navigation.steps.holder') +
          ': Geben Sie die Daten für die/den Versicherungsnehmer:in und die/den Beitragszahler:in an.'
      },
      {
        step: 3,
        label: t('navigation.steps.summary'),
        ariaDescription:
          t('navigation.steps.summary') +
          ': Prüfen Sie Ihre Angaben, laden Sie die Vertragsinformationen herunter und geben Sie die notwendigen Zustimmungen, damit Ihr Antrag eingereicht werden kann.'
      }
    ],
    []
  );

  return (
    <div>
      <WizardLayout>
        <QuestionsContextProvider
          getQuestionsByCriteria={fetchQuestionsFn}
          tariffs={selectedTariffs}
          birthDate={isOnInsuredPersonScreen ? birthDate : ''}
          insuranceStart={isOnInsuredPersonScreen ? insuranceStart : ''}
        >
          <WizardSlot.TopNavigation>
            <ProgressBar
              stepList={progressSteps}
              completeList={completeSections}
              active={currentSection}
              className="text-white-100 pl-[16px] m:pl-[80px]"
              rightContent={
                <div className="h-[28px] flex items-center gap-[16px]">
                  <ContactSupport />
                  <WhiteLogo />
                </div>
              }
              onSelectStep={(step) => handleProgressBarChange(step, goTo)}
            />
          </WizardSlot.TopNavigation>

          {isLoading ? <Loading /> : <WizardScreen currentScreen={currentScreen} />}
          <WizardSlot.RightPanel>
            <Sidebar />
          </WizardSlot.RightPanel>
          <WizardSlot.Footer>
            <LegalFooter />
          </WizardSlot.Footer>
        </QuestionsContextProvider>
      </WizardLayout>
    </div>
  );
}

export const DigitalOrderPage = () => {
  return (
    <WizardNavigationContextProvider>
      <HealthInsuranceContextProvider>
        <TariffContextProvider>
          <Content />
        </TariffContextProvider>
      </HealthInsuranceContextProvider>
    </WizardNavigationContextProvider>
  );
};
